import React, { Fragment, useCallback, useEffect, useMemo, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ModalAdmin from '../../Components/ModalAdmin';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';
import useStorage from '../../Hooks/useStorage';

import videosHelper from '../../Helpers/Videos';
import configuracionHelper from '../../Helpers/Configuracion';
import { recorrerPosiciones } from '../../Helpers/posiciones';
import { obtenerEmbedYoutube } from '../../Helpers/youtube';

// Campo de la colección de videos que corresponde a la página de Becas
const CAMPO_BECAS = '2';
// Los demás videos del campo son el principal ('mejor_opcion'), que es único y no se elimina
const GANADOR = 'beca_caav';

const NUEVO = { titulo: '', video: '', posicion: 1 };

const CAMPOS_CONVOCATORIA = ['beca_estado', 'beca_proxima_convocatoria', 'beca_monto', 'beca_tematica', 'beca_fecha_limite', 'beca_anio_bases'];

const ESTADOS = [
    { valor: 'proxima', etiqueta: 'Próxima convocatoria (solo aviso)' },
    { valor: 'informacion', etiqueta: 'Información (requisitos y PDF)' },
    { valor: 'abierta', etiqueta: 'Abierta (recibiendo videos)' }
];

// Tarjeta del panel de la convocatoria donde está cada campo, para resaltarla al seleccionarlo desde el preview
const SECCION_CAMPO = {
    beca_estado: 'estado',
    beca_proxima_convocatoria: 'estado',
    beca_monto: 'informacion',
    beca_tematica: 'informacion',
    beca_fecha_limite: 'recepcion',
    beca_anio_bases: 'recepcion'
};

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

// Solo los campos de la convocatoria: el PATCH de configuración no debe llevar los PDFs ni timestamps
const extraerConvocatoria = configuracion => Object.fromEntries(CAMPOS_CONVOCATORIA.map(campo => [campo, configuracion?.[campo] ?? '']));

const esGanador = video => video.vista === GANADOR;

const Becas = () => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [video, setVideo] = useState({ ...editar });
    const [nuevo, setNuevo] = useState(NUEVO);
    const [seccionActiva, setSeccionActiva] = useState(null);
    const [version, setVersion] = useState(0);
    // 'videos' o 'convocatoria': qué parte de la página muestra la vista previa
    const [modo, setModo] = useState('videos');

    const [convocatoriaGuardada, setConvocatoriaGuardada] = useState(null);
    const [convocatoria, setConvocatoria] = useState(extraerConvocatoria(null));
    const [becaPdf, setBecaPdf] = useState('');
    const [pdfActualizado, setPdfActualizado] = useState(null);
    const [modalPdf, setModalPdf] = useState(false);
    const [archivoPdf, setArchivoPdf] = useState(null);

    const [subiendo, setSubiendo] = useState(false);
    const [consulta, setConsulta] = useState('');
    const [respuestaStorage] = useStorage(consulta);

    useEffect(() => {
        obtenerDatos();
    }, []);

    useEffect(() => {
        setVideo({ ...editar });
    }, [editar]);

    useEffect(() => {
        if (respuestaStorage.action === 'agregarPdf') guardarPdf(respuestaStorage.url);
        if (respuestaStorage.action === 'eliminarSinConfirmar') setConsulta('');
    }, [respuestaStorage]);

    const obtenerDatos = async () => {
        const [resVideos, resConfiguracion] = await Promise.all([
            videosHelper.obtenerVideoCampo(CAMPO_BECAS),
            configuracionHelper.obtenerConfiguracion()
        ]);
        if (resVideos.success) {
            setData(resVideos.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener los videos, codigo: ' + resVideos.code, 'error');
        }
        if (resConfiguracion.success) {
            const datos = extraerConvocatoria(resConfiguracion.data);
            setConvocatoriaGuardada(datos);
            setConvocatoria(datos);
            setBecaPdf(resConfiguracion.data?.beca_pdf ?? '');
            setPdfActualizado(resConfiguracion.data?.updatedAt ?? null);
        } else {
            Swal.fire('Error!', 'No se pudo obtener la convocatoria, codigo: ' + resConfiguracion.code, 'error');
        }
    }

    const totalGanadores = data.filter(esGanador).length;
    const editandoPrincipal = Boolean(editar._id) && !esGanador(editar);

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(video) !== JSON.stringify(editar);
    const hayCambiosNuevo = Boolean(nuevo.titulo || nuevo.video);
    const hayCambiosConvocatoria = Boolean(convocatoriaGuardada) && JSON.stringify(convocatoria) !== JSON.stringify(convocatoriaGuardada);
    const hayCambios = hayCambiosEditar || hayCambiosNuevo || hayCambiosConvocatoria;

    // cambiarVideo modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNuevo(NUEVO);
        if (convocatoriaGuardada) setConvocatoria(convocatoriaGuardada);
        setSeccionActiva(null);
        setVersion(version => version + 1);
    }

    const panel = usePanelEdicion({ hayCambios, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if (hayCambios && !(await confirmarDescartar())) return false;
        limpiarBorradores();
        alAbrir?.();
        setModo(nombre === 'convocatoria' ? 'convocatoria' : 'videos');
        panel.abrir(nombre);
        return true;
    }

    const abrirAgregar = () => abrirPanel('agregar', () => setNuevo({ ...NUEVO, posicion: totalGanadores + 1 }));

    const verVideos = async () => {
        if (panel.abierto && !(await panel.cerrar())) return;
        setModo('videos');
    }

    const enfocarCampo = campo => {
        setSeccionActiva(SECCION_CAMPO[campo]);
        // El formulario se monta al abrir el panel; se espera un ciclo para que el input exista
        setTimeout(() => document.getElementById(campo)?.focus(), 100);
    }

    const seleccionar = async ({ id }) => {
        if (id === 'beca_pdf') return setModalPdf(true);
        if (CAMPOS_CONVOCATORIA.includes(id)) {
            if (panel.abierto === 'convocatoria' || (await abrirPanel('convocatoria'))) enfocarCampo(id);
            return;
        }
        const indice = data.findIndex(dato => dato._id === id);
        if (indice < 0 || (panel.abierto === 'editar' && indice === index)) return;
        abrirPanel('editar', () => {
            setEditar(data[indice]);
            setIndex(indice);
        });
    }

    const datosPreview = useMemo(() => {
        if (!convocatoriaGuardada) return null;
        const principales = data.filter(dato => !esGanador(dato));
        let ganadores = data.filter(esGanador);
        if (obtenerEmbedYoutube(nuevo.video)) {
            ganadores = recorrerPosiciones([...ganadores, { ...nuevo, vista: GANADOR, _id: 'nuevo' }], 'posicion', 'nuevo', nuevo.posicion);
        } else if (editar._id && esGanador(editar)) {
            ganadores = recorrerPosiciones(ganadores, 'posicion', editar._id, video.posicion);
        }
        return { videos: [...principales, ...ganadores], convocatoria: { ...convocatoria, beca_pdf: becaPdf } };
    }, [data, nuevo, editar, video.posicion, convocatoria, convocatoriaGuardada, becaPdf]);

    // seccion=videos | convocatoria: en preview el sitio pinta solo esa parte de la página
    const { iframeRef, src } = usePreviewSitio({
        page: 'becas',
        ruta: '/beca-caav?seccion=' + modo,
        data: datosPreview,
        onSelect: seleccionar
    });

    // Mientras el link no se reconozca, la vista previa conserva el último video válido
    const cambiarVideo = cambios => {
        setVideo(video => ({ ...video, ...cambios }));
        const visibles = { ...cambios };
        if ('video' in visibles && !obtenerEmbedYoutube(visibles.video)) delete visibles.video;
        setData(data => data.map((dato, i) => (i === index ? { ...dato, ...visibles } : dato)));
    }

    const cambiarNuevo = cambios => setNuevo(nuevo => ({ ...nuevo, ...cambios }));

    const cambiarConvocatoria = cambios => setConvocatoria(convocatoria => ({ ...convocatoria, ...cambios }));

    // Al agregar hay una posición más: el nuevo puede ir al final
    const maximaPosicion = esNuevo => totalGanadores + (esNuevo ? 1 : 0);

    const errores = (borrador, principal, maximo) => {
        const faltantes = [
            [principal || borrador.titulo?.trim(), 'Título'],
            [borrador.video?.trim(), 'Link de YouTube']
        ].filter(([valor]) => !valor).map(([, nombre]) => nombre);
        if (faltantes.length) return 'Faltan datos por llenar: ' + faltantes.join(', ');
        if (!obtenerEmbedYoutube(borrador.video)) return 'No se reconoce el enlace de YouTube';
        if (!principal && (!Number.isInteger(borrador.posicion) || borrador.posicion < 1 || borrador.posicion > maximo)) {
            return 'La posición debe ser un número entre 1 y ' + maximo;
        }
        return null;
    }

    const agregar = async e => {
        e.preventDefault();
        const error = errores(nuevo, false, maximaPosicion(true));
        if (error) return Swal.fire('Error!', error, 'error');
        setSubiendo(true);
        const res = await videosHelper.agregarVideo({ ...nuevo, campo: CAMPO_BECAS, video: obtenerEmbedYoutube(nuevo.video) });
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const actualizar = async e => {
        e.preventDefault();
        const error = errores(video, editandoPrincipal, maximaPosicion(false));
        if (error) return Swal.fire('Error!', error, 'error');
        setSubiendo(true);
        const res = await videosHelper.editarVideo({ ...video, video: obtenerEmbedYoutube(video.video) }, editar._id);
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar el video ' + editar.titulo + '?',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setSubiendo(true);
        const res = await videosHelper.eliminarVideo(editar._id);
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const erroresConvocatoria = () => {
        const { beca_estado, beca_monto } = convocatoria;
        const requeridos = beca_estado === 'proxima'
            ? [['beca_proxima_convocatoria', 'Próxima convocatoria']]
            : [
                ['beca_tematica', 'Temática'],
                ...(beca_estado === 'abierta' ? [['beca_fecha_limite', 'Fecha límite'], ['beca_anio_bases', 'Año de las bases']] : [])
            ];
        const faltantes = requeridos.filter(([campo]) => !String(convocatoria[campo] ?? '').trim()).map(([, nombre]) => nombre);
        if (faltantes.length) return 'Faltan datos por llenar: ' + faltantes.join(', ');
        if (beca_estado !== 'proxima' && (!Number.isInteger(beca_monto) || beca_monto < 0)) return 'El monto debe ser un número entero mayor o igual a 0';
        return null;
    }

    const guardarConvocatoria = async e => {
        e.preventDefault();
        const error = erroresConvocatoria();
        if (error) return Swal.fire('Error!', error, 'error');
        setSubiendo(true);
        const res = await configuracionHelper.editarConfiguracion({ ...convocatoria, beca_monto: Number(convocatoria.beca_monto) });
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const descartarConvocatoria = async () => {
        if (await confirmarDescartar()) setConvocatoria(convocatoriaGuardada);
    }

    const cerrarModalPdf = useCallback(() => {
        setModalPdf(false);
        setArchivoPdf(null);
    }, []);

    const subirPdf = e => {
        e.preventDefault();
        if (!archivoPdf) return Swal.fire('Error!', 'Selecciona un archivo PDF', 'error');
        setSubiendo(true);
        setConsulta({ consulta: 'agregarPdf', name: 'beca_' + Date.now(), folder: 'pdfs/becas/', file: archivoPdf, metadata: { contentDisposition: 'attachment; filename="convocatoria-gran-beca-caav.pdf"' } });
    }

    const guardarPdf = async url => {
        // useStorage solo vuelve a ejecutarse si cambia el texto de la consulta
        setConsulta('');
        if (!url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir el archivo', 'error');
        }
        const anterior = becaPdf;
        const res = await configuracionHelper.editarConfiguracion({ beca_pdf: url });
        setSubiendo(false);
        if (!res.success) {
            // Sin configuración que lo use, el PDF recién subido quedaría huérfano
            return setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: url });
        }
        // Se borra hasta que la configuración ya apunta al nuevo, para que el sitio nunca quede con un enlace roto
        if (anterior.includes('/o/pdfs%2Fbecas%2F')) {
            setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: anterior });
        }
        setBecaPdf(url);
        setPdfActualizado(res.data?.updatedAt ?? new Date().toISOString());
        cerrarModalPdf();
    }

    const seccion = clave => ({ id: clave, activa: seccionActiva === clave, onFocus: () => setSeccionActiva(clave) });

    const camposVideo = (borrador, cambiar, prefijo, principal) => {
        const id = campo => prefijo + campo;
        const esNuevo = prefijo === 'nuevo_';
        const linkInvalido = Boolean(borrador.video) && !obtenerEmbedYoutube(borrador.video);

        return (
            <Fragment>
                <SeccionPanel
                    numero={1}
                    titulo='Video'
                    ubicacion={principal ? 'Video grande de "Conoce al ganador de la Gran Beca CAAV Anual".' : 'Más ganadores de la Gran Beca CAAV Anual.'}
                    {...seccion('video')}
                >
                    {!principal && (
                        <Fragment>
                            <div className="input-field">
                                <input onChange={e => cambiar({ titulo: e.target.value })} value={borrador.titulo ?? ''} id={id('titulo')} type="text" className="validate" />
                                <label className='active' htmlFor={id('titulo')}>Título</label>
                            </div>
                            <p style={estiloAyuda}>Aparece debajo del video, ej. 2026.</p>
                        </Fragment>
                    )}
                    <div className="input-field">
                        <input
                            onChange={e => cambiar({ video: obtenerEmbedYoutube(e.target.value) ?? e.target.value })}
                            value={borrador.video ?? ''}
                            id={id('video')}
                            type="text"
                            className="validate"
                        />
                        <label className='active' htmlFor={id('video')}>Link de YouTube</label>
                    </div>
                    <p style={linkInvalido ? { ...estiloAyuda, color: "#c62828" } : estiloAyuda}>
                        {linkInvalido ? 'No se reconoce el enlace de YouTube' : 'Pega el link de YouTube (de la barra del navegador, de Compartir o de youtu.be)'}
                    </p>
                </SeccionPanel>

                {!principal && (
                    <SeccionPanel numero={2} titulo='Posición' ubicacion='Orden dentro de "Más ganadores".' {...seccion('posicion')}>
                        <div className="input-field">
                            <input
                                onChange={e => cambiar({ posicion: e.target.value === '' ? '' : Number(e.target.value) })}
                                value={borrador.posicion ?? 1}
                                id={id('posicion')}
                                type="number"
                                min="1"
                                max={maximaPosicion(esNuevo)}
                                className="validate"
                            />
                            <label className='active' htmlFor={id('posicion')}>Posición</label>
                        </div>
                        <p style={estiloAyuda}>1 = primero de la lista (de 1 a {maximaPosicion(esNuevo)}). Al cambiarla, los demás videos se recorren.</p>
                    </SeccionPanel>
                )}
            </Fragment>
        );
    }

    const inputConvocatoria = (campo, etiqueta, ayuda, maxLength = 200) => (
        <Fragment>
            <div className="input-field">
                <input onChange={e => cambiarConvocatoria({ [campo]: e.target.value })} value={convocatoria[campo] ?? ''} id={campo} type="text" className="validate" maxLength={maxLength} />
                <label className='active' htmlFor={campo}>{etiqueta}</label>
            </div>
            {ayuda && <p style={estiloAyuda}>{ayuda}</p>}
        </Fragment>
    );

    const { beca_estado } = convocatoria;

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar video'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNuevo}
                    pie={
                        <button type='submit' form='form_agregar_video' className='boton_admin' disabled={!hayCambiosNuevo}>
                            Agregar video
                        </button>
                    }
                >
                    {panel.abierto === 'agregar' && (
                        <form id='form_agregar_video' key={version} onSubmit={agregar}>
                            {camposVideo(nuevo, cambiarNuevo, 'nuevo_', false)}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo={editandoPrincipal ? 'Editar video principal' : 'Editar video'}
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            {!editandoPrincipal && (
                                <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                    Eliminar
                                </button>
                            )}
                            <button type='submit' form='form_editar_video' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'editar' && editar._id && (
                        <form id='form_editar_video' key={version} onSubmit={actualizar}>
                            {camposVideo(video, cambiarVideo, 'editar_', editandoPrincipal)}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'convocatoria'}
                    titulo='Convocatoria de la Gran Beca'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosConvocatoria}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartarConvocatoria} disabled={!hayCambiosConvocatoria}>
                                Descartar
                            </button>
                            <button type='submit' form='form_convocatoria_beca' className='boton_admin' disabled={!hayCambiosConvocatoria}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'convocatoria' && convocatoriaGuardada && (
                        <form id='form_convocatoria_beca' key={version} onSubmit={guardarConvocatoria}>
                            <SeccionPanel numero={1} titulo='Estado' ubicacion='Bloque "Gran Beca CAAV Anual".' {...seccion('estado')}>
                                <label htmlFor='beca_estado'>Qué se muestra</label>
                                <select
                                    id='beca_estado'
                                    className='browser-default'
                                    value={beca_estado}
                                    onChange={e => cambiarConvocatoria({ beca_estado: e.target.value })}
                                    style={{marginBottom:"20px"}}
                                >
                                    {ESTADOS.map(({ valor, etiqueta }) => (
                                        <option key={valor} value={valor}>{etiqueta}</option>
                                    ))}
                                </select>
                                {beca_estado === 'proxima' && inputConvocatoria('beca_proxima_convocatoria', 'Próxima convocatoria', 'Se ve como: Próxima convocatoria: …')}
                            </SeccionPanel>

                            {beca_estado !== 'proxima' && (
                                <SeccionPanel numero={2} titulo='Información' ubicacion='Descripción de la beca y tarjeta "Temática".' {...seccion('informacion')}>
                                    <div className="input-field">
                                        <input
                                            onChange={e => cambiarConvocatoria({ beca_monto: e.target.value === '' ? '' : Number(e.target.value) })}
                                            value={convocatoria.beca_monto ?? ''}
                                            id='beca_monto'
                                            type="number"
                                            min="0"
                                            className="validate"
                                        />
                                        <label className='active' htmlFor='beca_monto'>Monto de la beca</label>
                                    </div>
                                    <p style={estiloAyuda}>Se ve como: con valor de más de $… MXN.</p>
                                    <div className="input-field">
                                        <textarea
                                            onChange={e => cambiarConvocatoria({ beca_tematica: e.target.value })}
                                            value={convocatoria.beca_tematica ?? ''}
                                            id='beca_tematica'
                                            className="materialize-textarea"
                                            maxLength={1000}
                                            style={{minHeight:"120px"}}
                                        />
                                        <label className='active' htmlFor='beca_tematica'>Temática</label>
                                    </div>
                                    <p style={estiloAyuda}>Cada salto de línea se respeta en la página.</p>
                                </SeccionPanel>
                            )}

                            {beca_estado === 'abierta' && (
                                <SeccionPanel numero={3} titulo='Recepción de videos' ubicacion='Aviso de fecha límite y botón de las bases.' {...seccion('recepcion')}>
                                    {inputConvocatoria('beca_fecha_limite', 'Fecha límite', 'Se ve como: La fecha límite para mandar tu video es el …, hora del centro de México.')}
                                    {inputConvocatoria('beca_anio_bases', 'Año de las bases', 'Se ve como: Conoce las bases del …', 20)}
                                </SeccionPanel>
                            )}
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo={modo === 'videos' ? 'Vista previa de los videos de Becas' : 'Vista previa de la convocatoria de la Gran Beca'}
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Becas'
                    ayuda={modo === 'videos' ? 'Haz clic en un video para editarlo' : 'Haz clic en un texto o en el botón del PDF para editarlo'}
                    acciones={
                        <Fragment>
                            {modo === 'videos' ? (
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={() => abrirPanel('convocatoria')}>
                                    Convocatoria
                                </button>
                            ) : (
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={verVideos}>
                                    Ver videos
                                </button>
                            )}
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={() => setModalPdf(true)}>
                                PDF de la convocatoria
                            </button>
                            <button type='button' className='boton_admin' onClick={abrirAgregar}>
                                + Agregar video
                            </button>
                        </Fragment>
                    }
                >
                    <ModalAdmin abierto={modalPdf} onCerrar={cerrarModalPdf} titulo='PDF de la convocatoria'>
                        {becaPdf ? (
                            <p>
                                <a href={becaPdf} target="_blank" rel="noopener noreferrer">Descargar convocatoria actual</a>
                                {pdfActualizado && (
                                    <span style={{color:"#757575"}}> · actualizado el {new Date(pdfActualizado).toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' })}</span>
                                )}
                            </p>
                        ) : (
                            <p style={{color:"#757575"}}>Aún no se ha subido un PDF; el sitio usa el archivo incluido en el código.</p>
                        )}
                        <form onSubmit={subirPdf}>
                            <div className="file-field input-field">
                                <div className="btn" style={{backgroundColor:"#00496C"}}>
                                    <span>Archivo</span>
                                    <input onChange={e => setArchivoPdf(e.target.files[0] ?? null)} type="file" accept="application/pdf" />
                                </div>
                                <div className="file-path-wrapper">
                                    <input className="file-path" type="text" placeholder="Selecciona un PDF" value={archivoPdf?.name ?? ''} readOnly />
                                </div>
                            </div>
                            {becaPdf && (
                                <p style={{color:"#757575", fontSize:"13px", margin:"0"}}>Al reemplazarlo, el PDF anterior se elimina y el sitio descargará el nuevo.</p>
                            )}
                            <div style={{display:"flex", justifyContent:"flex-end", gap:"10px", marginTop:"10px"}}>
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={cerrarModalPdf}>
                                    Cancelar
                                </button>
                                <button type='submit' className='boton_admin' disabled={!archivoPdf}>
                                    {becaPdf ? 'Reemplazar PDF' : 'Subir PDF'}
                                </button>
                            </div>
                        </form>
                    </ModalAdmin>
                </PreviewSitio>
            </div>
        </Fragment>
    );
}

export default Becas;
